import { Image } from '@components/products/products'
import { API_URL } from '@constants'
import axiosReadInstance from '../../request/axiosReadInstance'
import { useEffect, useRef, useState } from 'react'
import 'moment/locale/fr'
import { ArticleC } from '@components/article/article'
import { replaceImageExtension } from '@utils'
import { usePageLoading } from '../../context/PageLoadingContext'

interface IUseCase {
    attributes: {
        title: string
        description: string
        image: Image
        client: string
        use_case_detail: {
            data: {
                id: any
            }
        }
    }
    id: any
}

interface IExpertises {
    attributes: {
        title: string
        description: string
        use_cases?: {
            data: Array<IUseCase>
        }
    }
    isFilter: Boolean
}

export const ExpertiseCases: React.FC = () => {
    const start: number = 0
    const initialLimit = 6
    const [limit, setLimit] = useState<number>(initialLimit)
    const pageSize: number = 6
    const [expertiseCases, setExpertiseCases] = useState<Array<IUseCase>>()
    const [expertiseCasesFiltered, setExpertiseCasesFiltered] =
        useState<Array<IUseCase>>()
    const [expertises, setExpertises] = useState<Array<IExpertises>>()
    const [fade, setFade] = useState(true)
    const [loadMore, setLoadMore] = useState<boolean>(true)
    const [noMore, setNoMore] = useState<boolean>(false)
    const hash = window.location.hash.substring(1)
    const { incrementLoadingRequestCount, decrementLoadingRequestCount } =
        usePageLoading()
    const [isInitialLoadUseCases, setIsInitialLoadUseCases] = useState(true)
    const [isInitialLoadCategories, setIsInitialLoadCategories] = useState(true)
    const pendingFocusCaseIndex = useRef<number | null>(null)
    const [loadMoreAnnouncement, setLoadMoreAnnouncement] = useState('')

    function handleClick(filter: IExpertises) {
        setFade(true)
        if (filter.isFilter) {
            filter.isFilter = false
            setExpertiseCasesFiltered(expertiseCases)
        } else {
            expertises?.forEach((element) => {
                if (element !== filter && element.isFilter) {
                    element.isFilter = false
                }
            })
            filter.isFilter = true
            setExpertiseCasesFiltered(filter.attributes.use_cases?.data)
        }
        setFade(false)
    }
    async function fetchData() {
        setFade(true)
        if (isInitialLoadUseCases) {
            incrementLoadingRequestCount()
        }
        await axiosReadInstance
            .get(
                `${API_URL}/api/use-cases?pagination[start]=` +
                    start +
                    `&pagination[limit]=` +
                    limit +
                    `&populate=*`
            )
            .then((response) => {
                if (limit >= response.data.meta.pagination.total) {
                    setNoMore(true)
                } else {
                    setNoMore(false)
                }
                setExpertiseCases(response.data.data)
                setExpertiseCasesFiltered(response.data.data)
            })
            .finally(() => {
                if (isInitialLoadUseCases) {
                    decrementLoadingRequestCount()
                    setIsInitialLoadUseCases(false)
                }
            })

        if (isInitialLoadCategories) {
            incrementLoadingRequestCount()
        }
        await axiosReadInstance
            .get(`${API_URL}/api/use-case-categories?populate=deep`)
            .then((response) => {
                setExpertises(response.data.data)
                expertises?.forEach((element) => {
                    element.isFilter = false
                })
            })
            .finally(() => {
                if (isInitialLoadCategories) {
                    decrementLoadingRequestCount()
                    setIsInitialLoadCategories(false)
                }
            })
        setFade(false)
    }

    useEffect(() => {
        if (hash.length > 0) {
            const selectedExpertise = expertises?.find(
                (expertise) => stringToSlug(expertise.attributes.title) === hash
            )
            if (selectedExpertise) {
                handleClick(selectedExpertise)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hash, expertises])

    useEffect(() => {
        setLoadMore(true)
        fetchData().then(() => {
            setLoadMore(false)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [limit])

    useEffect(() => {
        if (loadMore) return
        const idx = pendingFocusCaseIndex.current
        if (idx === null) return
        if (!expertiseCasesFiltered?.length) return
        pendingFocusCaseIndex.current = null
        const added = expertiseCasesFiltered.length - idx
        if (added <= 0) return
        const count = Math.min(pageSize, added)
        setLoadMoreAnnouncement(
            `${count} réalisation${
                count > 1 ? 's' : ''
            } supplémentaires chargées`
        )
        requestAnimationFrame(() => {
            document.getElementById(`expertise-case-link-${idx}`)?.focus()
        })
    }, [expertiseCasesFiltered, loadMore, pageSize])

    function stringToSlug(str: string) {
        return str
            .toLowerCase()
            .replace(/[^a-z0-9\s]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '')
    }

    return (
        <section className="py-6 px-6 sm:px-4">
            {expertises && (
                <div className="max-w-screen-xl mx-auto">
                    <p className="sr-only" aria-live="polite">
                        {loadMoreAnnouncement}
                    </p>
                    <div
                        className="flex flex-wrap gap-2 md:gap-1 md:flex-row justify-evenly"
                        role="group"
                        aria-label="Filtrer les réalisations par catégorie"
                    >
                        {expertises?.map((category, index) => {
                            return (
                                <button
                                    type="button"
                                    key={index}
                                    aria-pressed={Boolean(category.isFilter)}
                                    onClick={() => handleClick(category)}
                                    className={`rounded-full py-2 px-4 text-sm ${
                                        category.isFilter
                                            ? 'realisation-active'
                                            : 'realisation-inactive'
                                    }`}
                                >
                                    {category.attributes.title}
                                </button>
                            )
                        })}
                    </div>
                    <div
                        id="expertises"
                        className={`mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 auto-rows-auto  gap-x-6 gap-y-24 ${
                            fade ? 'animate-pulse' : ''
                        } `}
                    >
                        {expertiseCasesFiltered?.map((expertiseCase, index) => {
                            const url = `/realisation-detail/${expertiseCase.id}/${expertiseCase.attributes.use_case_detail.data.id}`

                            return (
                                <ArticleC
                                    url={url}
                                    title={expertiseCase.attributes.title}
                                    subtitle={expertiseCase.attributes.client}
                                    description={
                                        expertiseCase.attributes.description
                                    }
                                    srcImg={replaceImageExtension(
                                        expertiseCase.attributes.image.data
                                            .attributes.url
                                    )}
                                    altImg=""
                                    knowMoreText="En savoir plus"
                                    key={index}
                                    linkId={`expertise-case-link-${index}`}
                                />
                            )
                        })}
                    </div>

                    {loadMore && (
                        <div className="flex justify-center mt-10">
                            <svg
                                aria-hidden="true"
                                className="inline w-12 h-12 mr-2 animate-spin"
                                viewBox="0 0 100 101"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                    fill="#e5e7eb"
                                />
                                <path
                                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                    fill="#22c55e"
                                />
                            </svg>
                            <span className="sr-only">Loading...</span>
                        </div>
                    )}
                    <div className="flex justify-center">
                        {loadMore === false && noMore === false && (
                            <button
                                type="button"
                                className="button-secondary px-10 py-1 mt-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0a8335] focus-visible:outline-offset-2"
                                onClick={() => {
                                    pendingFocusCaseIndex.current = limit
                                    setLimit(limit + pageSize)
                                }}
                            >
                                Voir plus
                            </button>
                        )}
                    </div>
                </div>
            )}
        </section>
    )
}
